import type { Node } from 'react'
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Typography } from '@mui/material'
import { parseISO } from 'date-fns'
import { utcToZonedTime, format as formatTz } from 'date-fns-tz'
import * as route from '@app/lib/Statistic/statsRoutes'

import addDataPlaceholder from '@app/helpers/stats/addDeviceChartDataPlaceholder'
import handleErrors from '@app/helpers/handleErrors'
import { TIMEZONE_NUMBER_DEFINITION } from '@app/constants/stats'
import { lastReportRequest, statusLineRequest, deviceInfo } from '@app/logics/stats/stats'
import DeviceChart from '../Charts/DeviceChart'
import TimePicker from '../TimePicker/TimePicker'

import useStyles from './DeviceInfo.style'

type Props = {
  setError: () => void,
}

const DeviceInfo = (props: Props): Node => {
  const { classes } = useStyles()
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const { setError } = props
  const [statusLine, setStatusLine] = useState([
    [
      { type: 'string', id: 'Connection Type' },
      { type: 'string', id: 'Empty label' },
      { type: 'string', id: 'style', role: 'style' },
      {
        type: 'string', id: 'tooltip', role: 'tooltip', p: { html: true },
      },
      { type: 'date', id: 'Start' },
      { type: 'date', id: 'End' },
    ],
  ])
  const [currentTimezone, setCurrentTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [lastReport, setLastReport] = useState({
    protocol_carrier_key: '',
    last_report_time: '',
  })
  const [showReport, setShowReport] = useState(false)
  const [deviceSsid, setDeviceSsid] = useState(null)
  const [deviceSerialNumber, setDeviceSerialNumber] = useState(null)

  const getStatusLine = async (startDate: Date, endDate: Date, timezone: string) => {
    try {
      const tz = timezone.slice(TIMEZONE_NUMBER_DEFINITION)
      const response = await statusLineRequest(
        formatTz(startDate, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone: tz }),
        formatTz(endDate, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone: tz }),
        deviceId,
      )
      setCurrentTimezone(tz)
      setStatusLine((prevState) => [
        prevState[0],
        ...(response.data.status_lines.length ? addDataPlaceholder(response.data.status_lines[0].start_at, timezone) : []),
        ...response.data.status_lines.map((line) => {
          const lineStart = typeof line.start_at === 'string' ? parseISO(line.start_at) : line.start_at
          const lineFinish = typeof line.finish_at === 'string' ? parseISO(line.finish_at) : line.finish_at
          return [
            I18n.t(`stats.${line.protocol_carrier_key}`),
            '',
            null,
            null,
            utcToZonedTime(lineStart, tz),
            utcToZonedTime(lineFinish, tz),
          ]
        }),
      ])
    } catch (err) {
      setError(handleErrors(err))
    }
  }

  const getLastReport = async () => {
    try {
      const response = await lastReportRequest(
        deviceId,
      )
      if (response.data.last_report) {
        setLastReport({
          protocol_carrier_key: response.data.last_report.protocol_carrier_key,
          time: response.data.last_report.time,
        })
      }
      setShowReport(true)
    } catch (err) {
      setError(handleErrors(err))
    }
  }

  const getDeviceInfo = async () => {
    try {
      const response = await deviceInfo(deviceId)
      if (response.data.unit_device) {
        setDeviceSsid(response.data.unit_device.ssid)
        setDeviceSerialNumber(response.data.unit_device.serial_number)
      }
    } catch (err) {
      setError(handleErrors(err))
    }
  }

  const backToMainPage = () => {
    navigate(route.STATS_MAIN_PAGE_REDIRECT)
  }

  getDeviceInfo()
  return (
    <>
      <Typography variant="h3" className={classes.deviceTitle}>
        {I18n.t('stats.time_line_chart_title')}
      </Typography>
      <Typography variant="h4" className={classes.deviceTitle}>
        SSID:
        {' '}
        {deviceSsid || I18n.t('my_devices.not_available')}
      </Typography>
      <Typography variant="h4" className={classes.deviceTitle}>
        Serial Number:
        {' '}
        {deviceSerialNumber || I18n.t('my_devices.not_available')}
      </Typography>
      <TimePicker
        onSubmitHandler={getStatusLine}
      />
      { statusLine.length > 1 ? <DeviceChart statusLine={statusLine} /> : (
        <div className={classes.chartPlaceholder}>
          <Typography>{I18n.t('stats.choose_different_time')}</Typography>
        </div>
      )}
      <div className={classes.reportContainer}>
        <Typography variant="h6">{I18n.t('stats.last_report')}</Typography>
        <hr />
        <div className={classes.reportControls}>
          <Typography className={classes.report}>
            {showReport && (lastReport.protocol_carrier_key
              ? (() => {
                const reportTime = typeof lastReport.time === 'string'
                  ? parseISO(lastReport.time) : lastReport.time
                const zonedTime = utcToZonedTime(reportTime, currentTimezone)
                const formatted = formatTz(zonedTime, 'do MMM yy, h:mm:ss a', {
                  timeZone: currentTimezone,
                })
                return `${I18n.t(`stats.${lastReport.protocol_carrier_key}`)} at ${formatted}`
              })()
              : I18n.t('stats.never_online'))}
            {showReport ? '' : I18n.t('stats.press_show')}
          </Typography>
          <Button size="large" color="primary" variant="contained" onClick={getLastReport}>{I18n.t('buttons.show')}</Button>
        </div>
      </div>
      <div className={classes.buttonWrapper}>
        <Button size="large" color="primary" variant="contained" onClick={backToMainPage}>{I18n.t('stats.back_to_main')}</Button>
      </div>
    </>
  )
}

export default DeviceInfo
